import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { Box, Stack, Typography, Card, Chip, Button, Tooltip, IconButton } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import EditIcon from "@mui/icons-material/Edit";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { tokens } from "../../theme";
import { pageContainerSx } from "../../components/ui";

// No billing backend yet: the plan, card and invoice data below is placeholder,
// badged "Demo data" so it is never mistaken for real billing information.
const PLAN = {
  name: "Fleet Pro",
  detail: "6 robots · unlimited maps & venues",
  price: "₹48,000",
  nextBilling: "01 Aug 2026",
};
const CARD = {
  last4: "4291",
  brand: "HDFC Visa",
  exp: "08/28",
  email: "billing@botriq.com",
  gstin: "06AAB…Z5",
};
const INVOICES = [
  {
    id: "INV-2026-08",
    date: "01 Aug 2026",
    plan: "Fleet Pro · 6 robots",
    amount: "₹48,000",
    status: "upcoming",
  },
  {
    id: "INV-2026-07",
    date: "01 Jul 2026",
    plan: "Fleet Pro · 6 robots",
    amount: "₹48,000",
    status: "paid",
  },
  {
    id: "INV-2026-06",
    date: "01 Jun 2026",
    plan: "Fleet Pro · 6 robots",
    amount: "₹48,000",
    status: "paid",
  },
  {
    id: "INV-2026-05",
    date: "01 May 2026",
    plan: "Fleet Pro · 5 robots",
    amount: "₹40,000",
    status: "paid",
  },
];

// Explicit `id` per column: the trailing download column has an empty label, so
// keying header cells on label text alone would collide.
const INVOICE_COLUMNS = [
  { id: "invoice", label: "Invoice" },
  { id: "date", label: "Date" },
  { id: "plan", label: "Plan" },
  { id: "amount", label: "Amount" },
  { id: "status", label: "Status" },
  { id: "actions", label: "" },
];

const statusChip = (status) => {
  const map = {
    paid: ["Paid", tokens.success, tokens.successSoft],
    upcoming: ["Upcoming", tokens.warning, tokens.warningSoft],
    due: ["Due", tokens.danger, tokens.dangerSoft],
  };
  const [label, color, bg] = map[status] || map.due;
  return (
    <Chip
      size="small"
      label={label}
      sx={{ height: 20, fontSize: 11, fontWeight: 600, color, background: bg }}
    />
  );
};

const MiniStat = ({ label, value }) => (
  <Box>
    <Typography sx={{ fontSize: 11, color: tokens.text3 }}>{label}</Typography>
    <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: tokens.text, mt: 0.25 }}>
      {value}
    </Typography>
  </Box>
);

// `embedded` renders this inside the Settings tab pane (no self-contained page
// chrome). Default/false keeps the standalone /payment route identical.
MiniStat.propTypes = {
  label: PropTypes.node,
  value: PropTypes.node,
};

const Payment = ({ embedded = false }) => {
  const navigate = useNavigate();
  return (
    <Box sx={pageContainerSx({ embedded })}>
      {!embedded && (
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/settings")}
          size="small"
          sx={{
            mb: 1.5,
            color: tokens.text2,
            "&:hover": { color: tokens.text, background: tokens.surface2 },
          }}
        >
          Back to Settings
        </Button>
      )}
      {/* Standalone shows the full page header (with the "Demo data" badge);
        embedded drops it (the tab bar names the section). */}
      {!embedded && (
        <Stack direction="row" alignItems="center" gap={1.75} flexWrap="wrap" sx={{ mb: 2 }}>
          <Box>
            <Stack direction="row" alignItems="center" gap={1}>
              <Typography variant="h1">Payment</Typography>
              <Tooltip title="Placeholder data — billing is not yet connected" arrow>
                <Chip
                  size="small"
                  icon={<InfoOutlinedIcon sx={{ fontSize: "13px !important" }} />}
                  label="Demo data"
                  sx={{
                    height: 22,
                    fontSize: 11,
                    fontWeight: 600,
                    background: tokens.surface3,
                    color: tokens.text2,
                  }}
                />
              </Tooltip>
            </Stack>
            <Typography sx={{ fontSize: 13, color: tokens.text2, mt: 0.4 }}>
              Your plan, payment method and invoices
            </Typography>
          </Box>
        </Stack>
      )}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 1.75,
          mb: 1.75,
        }}
      >
        {/* Current plan */}
        <Card sx={{ p: 2 }}>
          <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1.5 }}>
            <Chip
              size="small"
              label="Current plan"
              sx={{
                height: 20,
                fontSize: 11,
                fontWeight: 600,
                background: tokens.accentSoft,
                color: tokens.accent2,
              }}
            />
            <Chip
              size="small"
              label="Active"
              icon={
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    background: tokens.success,
                    ml: "8px !important",
                  }}
                />
              }
              sx={{
                height: 20,
                fontSize: 11,
                fontWeight: 600,
                color: tokens.success,
                background: tokens.successSoft,
                "& .MuiChip-label": { pl: 0.5 },
              }}
            />
          </Stack>
          <Typography
            sx={{ fontSize: 24, fontWeight: 700, letterSpacing: "-.02em", color: tokens.text }}
          >
            {PLAN.name}
          </Typography>
          <Typography sx={{ fontSize: 13, color: tokens.text2, mt: 0.25 }}>
            {PLAN.detail}
          </Typography>
          <Stack direction="row" alignItems="baseline" gap={0.75} sx={{ mt: 1.75 }}>
            <Typography sx={{ fontSize: 28, fontWeight: 700, color: tokens.text }}>
              {PLAN.price}
            </Typography>
            <Typography sx={{ fontSize: 13, color: tokens.text3 }}>/ month</Typography>
          </Stack>
          <Typography sx={{ fontSize: 12, color: tokens.text3, mt: 0.75 }}>
            Next billing · {PLAN.nextBilling}
          </Typography>
          <Stack direction="row" gap={1} sx={{ mt: 2 }}>
            <Button
              variant="outlined"
              size="small"
              sx={{ borderColor: tokens.border, color: tokens.text }}
            >
              Change plan
            </Button>
            <Button size="small" sx={{ color: tokens.text2 }}>
              Cancel plan
            </Button>
          </Stack>
        </Card>

        {/* Payment method */}
        <Card sx={{ overflow: "hidden" }}>
          <Stack
            direction="row"
            alignItems="center"
            sx={{ p: "13px 16px", borderBottom: `1px solid ${tokens.border}` }}
          >
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: tokens.text }}>
              Payment method
            </Typography>
            <Button
              size="small"
              startIcon={<EditIcon sx={{ fontSize: "14px !important" }} />}
              sx={{ ml: "auto", color: tokens.text2 }}
            >
              Update
            </Button>
          </Stack>
          <Box sx={{ p: 2 }}>
            <Stack
              direction="row"
              alignItems="center"
              gap={1.5}
              sx={{
                p: 1.75,
                borderRadius: "12px",
                background: "linear-gradient(135deg, #3d6ae0, #2f56c0)",
                color: "#fff",
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 28,
                  borderRadius: "6px",
                  background: "rgba(255,255,255,0.25)",
                  display: "grid",
                  placeItems: "center",
                }}
              >
                <CreditCardIcon sx={{ fontSize: 18, color: "#fff" }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography
                  sx={{
                    fontSize: 14,
                    fontWeight: 700,
                    fontFamily: "'JetBrains Mono',monospace",
                    letterSpacing: ".06em",
                  }}
                >
                  •••• {CARD.last4}
                </Typography>
                <Typography sx={{ fontSize: 11.5, opacity: 0.9 }}>
                  {CARD.brand} · exp {CARD.exp}
                </Typography>
              </Box>
            </Stack>
            <Stack direction="row" gap={2.5} sx={{ mt: 1.75 }}>
              <MiniStat label="Billing email" value={CARD.email} />
              <MiniStat label="GSTIN" value={CARD.gstin} />
            </Stack>
          </Box>
        </Card>
      </Box>

      {/* Invoices */}
      <Card sx={{ overflow: "hidden" }}>
        <Box sx={{ p: "13px 16px", borderBottom: `1px solid ${tokens.border}` }}>
          <Typography sx={{ fontSize: 13, fontWeight: 600, color: tokens.text }}>
            Invoices{" "}
            <Box component="span" sx={{ color: tokens.text3, fontWeight: 400 }}>
              · {INVOICES.length} records
            </Box>
          </Typography>
        </Box>
        <Box sx={{ overflowX: "auto" }}>
          <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", minWidth: 620 }}>
            <Box component="thead">
              <Box component="tr">
                {INVOICE_COLUMNS.map(({ id, label }, i) => (
                  <Box
                    component="th"
                    key={id}
                    sx={{
                      textAlign: i >= 3 ? "right" : "left",
                      px: 1.75,
                      height: 38,
                      fontSize: 10.5,
                      fontWeight: 600,
                      letterSpacing: ".05em",
                      textTransform: "uppercase",
                      color: tokens.text3,
                      background: tokens.surface2,
                      borderBottom: `1px solid ${tokens.border}`,
                    }}
                  >
                    {label}
                  </Box>
                ))}
              </Box>
            </Box>
            <Box component="tbody">
              {INVOICES.map((v, i) => (
                <Box
                  component="tr"
                  key={v.id}
                  sx={{
                    height: 48,
                    borderBottom: i < INVOICES.length - 1 ? `1px solid ${tokens.border}` : "none",
                  }}
                >
                  <Box
                    component="td"
                    sx={{
                      px: 1.75,
                      fontSize: 12.5,
                      fontWeight: 600,
                      fontFamily: "'JetBrains Mono',monospace",
                      color: tokens.text,
                    }}
                  >
                    {v.id}
                  </Box>
                  <Box component="td" sx={{ px: 1.75, fontSize: 12.5, color: tokens.text2 }}>
                    {v.date}
                  </Box>
                  <Box component="td" sx={{ px: 1.75, fontSize: 12.5, color: tokens.text2 }}>
                    {v.plan}
                  </Box>
                  <Box
                    component="td"
                    sx={{
                      px: 1.75,
                      fontSize: 12.5,
                      textAlign: "right",
                      fontWeight: 600,
                      color: tokens.text,
                    }}
                  >
                    {v.amount}
                  </Box>
                  <Box component="td" sx={{ px: 1.75, textAlign: "right" }}>
                    <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                      {statusChip(v.status)}
                    </Box>
                  </Box>
                  <Box component="td" sx={{ px: 1.75, textAlign: "right" }}>
                    <IconButton size="small" sx={{ color: tokens.text3 }}>
                      <DownloadIcon sx={{ fontSize: 15 }} />
                    </IconButton>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      </Card>
    </Box>
  );
};

Payment.propTypes = {
  embedded: PropTypes.bool,
};

export default Payment;
